/* eslint-disable max-lines */
import React, { useState } from "react";
import { useParams } from 'react-router-dom';
import { DoseDescription, Drug, Item } from "server-openapi";
import styled from 'styled-components';
import { apis } from '../../../../core/mrs/apis';
import { createDataSource, DataTable } from '../../../../kit/DataTable/DataTable';
import { AsyncResource, useAsync } from '../../../../kit/hooks/UseAsync';
import { Layout } from '../../../../kit/Layout';
import { parseInt } from "lodash-es";
import { GetFacilityMedicationChangesResponse } from "server-openapi/dist/generated-sdk/api";
import { SelectLikeTextInput } from "../../../../kit/Forms/SelectLikeTextInput";
import { DateUtils } from "../../../../core/utils/dateUtils";
import { DrugUtils } from "../../../../syncstream/utils/DrugUtils";
import { Text } from "../../../../kit/Text";
import { DrugWarningIcons } from "../../../../pages/ResidentDetails/components/medicationInformation/DrugDetails";
import { format } from "date-fns";
import Badge from "../../../../kit/Badge";
import Loader from "react-loader-spinner";

interface IParams {
    facilityGroupId: string;
}
interface IProps {
    selectedStartMonth?: Date;
    shouldSearch: boolean;
}

export function FacilityMedicationChangesReportTable(props: IProps) {
    const facilityGroupId = parseInt(useParams<IParams>().facilityGroupId);
    const [wardFilter, setWardFilter] = useState<string>();
    const [patientFilter, setPatientFilter] = useState<string>();
    const [medicationFilter, setMedicationFilter] = useState<string>();

    const facilityMedicationChanges = useAsync(async () => {

        if(!props.shouldSearch || !props.selectedStartMonth){
            return null;
        }

        // Use the current selected month for the new search
        const startDateMonth = props.selectedStartMonth?.toDateString(); // toISOString();

        // Get the facility medication changes for the selected month
        return (await apis.facilityMedicationChangesReports.facilityMedicationChangesReportGetFacilityMedicationChanges(
            facilityGroupId,
            startDateMonth)).data;
    }, [facilityGroupId, props.selectedStartMonth, props.shouldSearch]); // Add selectedStartMonth and shouldSearch to dependencies

    const displayMonth = props.selectedStartMonth ? format(props.selectedStartMonth, 'MMMM yyyy') : '';

    const dataSource = createFacilityMedicationChangesTableData(
        facilityMedicationChanges,
        wardFilter,
        patientFilter,
        medicationFilter
    );

    // Show loading spinner when loading is true
    if (facilityMedicationChanges.loading || facilityMedicationChanges.error !== undefined) {
        return (
            <Layout horizontal style={{ justifyContent: 'center', alignItems: 'center', padding: '2rem' }}>
                {facilityMedicationChanges.loading && <Loader type="Oval" color="#005555" height={80} width={80} />}
                {facilityMedicationChanges.error !== undefined ? <Text style={{ color: 'white' }}>{facilityMedicationChanges.error.message}</Text> : null}
            </Layout>
        );
    }

    // Show message when there are no items
    if (props.shouldSearch && !facilityMedicationChanges.value?.items?.length) {
        return (
            <Layout horizontal style={{ justifyContent: 'center', alignItems: 'center', padding: '2rem' }}>
                <span style={{ color: 'white' }}>No medication changes found for the selected period</span>
            </Layout>
        );
    }

    return (facilityMedicationChanges.value?.items && facilityMedicationChanges.value.items.length > 0 ?
        <>
            <HeaderSection
                wardFilter={wardFilter}
                setWardFilter={setWardFilter}
                patientFilter={patientFilter}
                setPatientFilter={setPatientFilter}
                medicationFilter={medicationFilter}
                setMedicationFilter={setMedicationFilter}
            />
            {props.selectedStartMonth && (
                <Text style={{ marginTop: '1rem', marginBottom: '-3rem', color:'white' }}>
                    Showing results for: {displayMonth}
                </Text>
            )}
            <TableContainer>
                <DataTable
                    datasource={dataSource}
                    filters={[]}
                    defaultPageSize={12}
                    itemLabel={'changes'}
                    countTextColour={'white'}
                    pagingTextColour={'white'}/>
            </TableContainer>
        </>
        :
        <></>
    );
}

function createFacilityMedicationChangesTableData(
    facilityMedicationChanges: AsyncResource<GetFacilityMedicationChangesResponse | null>,
    wardFilter?: string,
    patientFilter?: string,
    medicationFilter?: string
) {
    const tableData = (facilityMedicationChanges.value?.items ?? [])
        .filter((item) => wardFilter === undefined || item.ward_name?.toLowerCase().includes(wardFilter.toLowerCase()))
        .filter((item) => patientFilter === undefined || item.full_name?.toLowerCase().includes(patientFilter.toLowerCase()))
        .filter((item) => medicationFilter === undefined
            || item.drug?.drug_description?.toLowerCase().includes(medicationFilter.toLowerCase())
            || item.drug?.alternative_name?.toLowerCase().includes(medicationFilter.toLowerCase()))
        .map((item) => {
            return {
                col1: (
                    <span>
                        {item.facility_name}
                    </span>
                ),
                col2: (
                    <span>
                        {item.ward_name}
                    </span>
                ),
                col3: (
                    <span>
                        {item.full_name}
                    </span>
                ),
                col4: (
                    item.drug ? <DrugDetailsLabel drug={item.drug} /> : null
                ),
                col5: (
                    <DirectionsLabelComponent item={item} />
                ),
                col6: (
                    <Layout gap={1}>
                        <span>
                            {item.changed_by}
                        </span>
                        <span style={{fontWeight: 'lighter'}}>
                            {item.changed_at ? DateUtils.formatDateTimeWithAMPM(item.changed_at) : ''}
                        </span>
                    </Layout>
                )
            };
        });

    return createDataSource(tableData, () => [
        { accessor: (data) => data.col1, Header: 'Facility', width: '130px' },
        { accessor: (data) => data.col2, Header: 'Ward', width: '150px'},
        { accessor: (data) => data.col3, Header: 'Name', width: '150px' },
        { accessor: (data) => data.col4, Header: 'Medication', width: '200px'},
        { accessor: (data) => data.col5, Header: 'Directions', width: '200px'},
        { accessor: (data) => data.col6, Header: 'Changed By', width: '200px'},
    ]);
}

function HeaderSection(props: {
    setWardFilter: (ward: string | undefined) => void;
    wardFilter?: string;
    setPatientFilter: (patient: string | undefined) => void;
    patientFilter?: string;
    setMedicationFilter: (medication: string | undefined) => void;
    medicationFilter?: string;
}) {
    return (
        <HeaderContainer>
            <FilterContainer>
                <TextFilterContainer gap={0.3}>
                    <SelectLikeTextInput
                        fullWidth
                        name="wardFilter"
                        placeholder="Ward"
                        value={props.wardFilter ?? ''}
                        onChange={(v) => props.setWardFilter(v)}
                        onClear={() =>  props.setWardFilter(undefined)}
                        height={38}
                    />
                </TextFilterContainer>
                <TextFilterContainer gap={0.5}>
                    <SelectLikeTextInput
                        fullWidth
                        name="patientFilter"
                        placeholder="Patient"
                        value={props.patientFilter ?? ''}
                        onChange={(v) => props.setPatientFilter(v)}
                        onClear={() => props.setPatientFilter(undefined)}
                        height={38}
                    />
                </TextFilterContainer>
                <TextFilterContainer gap={0.5}>
                    <SelectLikeTextInput
                        fullWidth
                        name="medicationFilter"
                        placeholder="Medication"
                        value={props.medicationFilter ?? ''}
                        onChange={(v) => props.setMedicationFilter(v)}
                        onClear={() => props.setMedicationFilter(undefined)}
                        height={38}
                    />
                </TextFilterContainer>
            </FilterContainer>
        </HeaderContainer>
    );
}

interface DrugDetailsLabelProps {
    drug: Drug;
}

const getDrugTextColor = (drug: Drug): string | undefined => {
    const warnings = DrugUtils.getDrugWarningsFromDrug(drug);
    return DrugUtils.getTextColour(warnings);
};

function DrugDetailsLabel({ drug }: DrugDetailsLabelProps) {
    const drugWarnings = DrugUtils.getDrugWarningsFromDrug(drug);
    const textColor = getDrugTextColor(drug);

    return (
        <Layout gap={1}>
            <DrugNameContainer horizontal>
                <DrugWarningIcons drugWarnings={drugWarnings} />
                <DrugName warningColor={textColor}>
                    {drug.drug_description}
                </DrugName>
            </DrugNameContainer>
            <AlternativeName>
                {drug.alternative_name}
            </AlternativeName>
        </Layout>
    );
}

const DirectionsWithBadge = ({ text, directions, doseDescriptions, isCeased = false, style }:
{
    text: string;
    directions: string;
    doseDescriptions: DoseDescription[];
    isCeased?: boolean;
    style?: React.CSSProperties;
}) => (
    <Layout gap={0.2} style={style}>
        <Layout horizontal>
            <Badge type={'info'} text={text} minWidth="45px" marginRight="12px" marginLeft="0" />
            {isCeased ? <del>{directions}</del> : <span>{directions}</span>}
        </Layout>
        <Layout style={{ marginLeft: text.length < 4 ? '57px' : '65px' }}>  {/* 45px (Badge width) + 12px (marginRight) */}
            {doseDescriptions.map((dose, index) => (
                isCeased ?
                    <del style={{ fontWeight: 'lighter' }} key={index}>{dose.description}</del> :
                    <span style={{ fontWeight: 'lighter' }} key={index}>{dose.description}</span>
            ))}
        </Layout>
    </Layout>
);

interface DirectionsLabelProps {
    item: Item;
}

const DirectionsLabelComponent: React.FC<DirectionsLabelProps> = ({ item }) => {
    const isStringEmpty = (str: string | null | undefined): boolean => !str?.trim();

    const renderDirectionsWithStatus = (directions: string, doseDescriptions: DoseDescription[], status: string, isCeased: boolean) => (
        isCeased ? (
            <DirectionsWithBadge text="ceased" directions={directions} doseDescriptions={doseDescriptions} isCeased />
        ) : (
            <>
                <Layout gap={0.2}>
                    <span>{directions}</span>
                    {doseDescriptions.map((dose, index) => (
                        <span style={{ fontWeight: 'lighter' }} key={index}>{dose.description}</span>
                    ))}
                </Layout>
                <span style={{ fontWeight: 'lighter' }}>{status}</span>
            </>
        )
    );

    const renderOldAndNewDirections = (newDirections: string, oldDirections: string, newDoseDescriptions: DoseDescription[], oldDoseDescriptions: DoseDescription[]) => (
        <span>
            <DirectionsWithBadge
                text="new"
                directions={newDirections}
                doseDescriptions={newDoseDescriptions}
                style={{ marginBottom: '1rem' }}
            />
            <DirectionsWithBadge
                text="old"
                directions={oldDirections}
                doseDescriptions={oldDoseDescriptions}
                isCeased />
        </span>
    );

    const { new_directions, old_directions, new_dose_descriptions, old_dose_descriptions, change_status } = item;
    const hasNewDirections = !isStringEmpty(new_directions);
    const hasOldDirections = !isStringEmpty(old_directions);
    const isCeased = change_status?.toLowerCase() === 'ceased';

    if (!hasNewDirections && !hasOldDirections) {
        return null;
    }

    return (
        <Layout gap={1}>
            {hasNewDirections && hasOldDirections ? (
                renderOldAndNewDirections(new_directions || '', old_directions || '', new_dose_descriptions || [], old_dose_descriptions || [])
            ) : hasNewDirections ? (
                renderDirectionsWithStatus(new_directions || '', new_dose_descriptions || [], change_status || '', isCeased)
            ) : null}
        </Layout>
    );
};

const FilterContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 20px;
`;

const HeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const TableContainer = styled.div`
  margin-top: 30px;
`;

const TextFilterContainer = styled(Layout)`
  background: ${(p) => p.theme.backgrounds.lighter.bg};
  width: 375px;

  & .input-label {
    font-size: 12px;
    font-weight: bold;
  }
`;

const DrugNameContainer = styled(Layout)`
    display: flex;
    align-items: center;
`;

const DrugName = styled(Text)<{ warningColor?: string }>`
    color: ${({ warningColor }) => warningColor};
    font-weight: bold;
`;

const AlternativeName = styled.span`
    font-weight: lighter;
`;